import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createProjectPath } from '@constant/gridgio/route-path';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/gridgio/modal-slice';
import styled from 'styled-components';
import Scroll from '@component/common/scroll';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import Chip from '@design-system/chip';
import CollectionPlaceholder from '@design-system/collection-placeholder';
import Canvas from '@design-system/canvas';
import Skeleton from '@design-system/skeleton';
import { useProjectQuery } from '@hook/gridgio/useApi';
import PlanThumbnail from '@component/gridgio/project/plan/plan-thumbnail';
import type { IPlanData } from '@datatype/gridgio/api-type';
import type { IStartWorkModalData } from '@datatype/gridgio/type';
import { ETheme } from '@datatype/type';
import * as color from '@constant/color';
import * as size from '@constant/size';

interface IProps {
  data: IStartWorkModalData['data'];
}

const PlanThumbnailContainer = styled.div`
  width: 75px;
  height: 50px;
  overflow: hidden;
  border-radius: ${size.BORDER_RADIUS_1};
`;

const Wrapper = styled.div`
  display: flex;
  width: ${size.GRIDGIO_LARGE_MODAL_WIDTH};
  flex-direction: column;
`;

const PlanItemsContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  gap: ${size.MARGIN_1};
`;

const PlanItemContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: ${size.MARGIN_1};
`;
const PlanInfoContainer = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 5px;
`;

const StartWorkModal: React.FC<IProps> = ({ data }) => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState<'method' | 'plan'>(data.initialStep ?? 'method');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const projectQuery = useProjectQuery(data.projectKey);
  const plans = projectQuery.data?.data.plans ?? [];
  const handleSelect = (plan: IPlanData) => {
    dispatch(modalActions.showDialogModal({ type: 'create-work', data: { projectKey: data.projectKey, plan } }));
  };
  const PlanItems = plans.map((plan) => {
    const source = plan.source;
    return (
      <Canvas
        key={plan.key}
        width='100%'
        padding={size.MARGIN_1}
        theme={ETheme.WHITE}
        border
        handleClick={() => handleSelect(plan)}>
        <PlanItemContainer>
          <PlanThumbnailContainer>
            <PlanThumbnail source={source} preview={false} />
          </PlanThumbnailContainer>
          <PlanInfoContainer>
            <TextBlock
              text={plan.name ?? ''}
              design={{
                box: { width: '100%', alignX: 'left' },
                text: { size: 'medium', color: color.BLACK_2, bold: true, oneline: true },
              }}
            />
            <TextBlock
              text={source.filename}
              design={{
                box: { width: '100%', alignX: 'left' },
                text: { size: 'small', color: color.GRAY_1, oneline: true },
              }}
            />
          </PlanInfoContainer>
        </PlanItemContainer>
      </Canvas>
    );
  });

  return (
    <Wrapper>
      {step === 'method' && (
        <>
          <TextBlock text='새 작업' design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }} />
          <Gap height={size.MARGIN_1} />
          <TextBlock text='작업을 어떻게 시작할까요?' design={{ text: { size: 'medium', color: color.GRAY_1 } }} />
          <Gap height={size.MARGIN_2} />
          <Canvas
            theme={ETheme.WHITE}
            width='100%'
            padding={size.MARGIN_1}
            alignX='left'
            border
            handleClick={() => setStep('plan')}>
            <TextBlock
              text='새로 기록하기'
              design={{ box: { alignX: 'left' }, text: { size: 'medium', color: color.BLACK_2, bold: true } }}
            />
            <Gap height={'5px'} />
            <TextBlock
              text='등록된 도면을 선택해 손상을 직접 표시합니다.'
              design={{ box: { width: '100%', alignX: 'left' }, text: { size: 'small', color: color.BLACK_5 } }}
            />
          </Canvas>
          <Gap height={size.MARGIN_1} />
          <Canvas
            theme={ETheme.WHITE}
            width='100%'
            padding={size.MARGIN_1}
            alignX='left'
            border
            handleClick={() =>
              dispatch(
                modalActions.showDialogModal({
                  type: 'feature-planned',
                  data: {
                    returnTo: { type: 'start-work', data: { projectKey: data.projectKey, initialStep: 'method' } },
                  },
                }),
              )
            }>
            <TextBlock
              text='DWG에서 가져오기'
              design={{ box: { alignX: 'left' }, text: { size: 'medium', color: color.BLACK_2, bold: true } }}
            />
            <Gap height={'5px'} />
            <TextBlock
              text='DWG에 기록된 손상정보를 도면으로 가져옵니다.'
              design={{ box: { width: '100%', alignX: 'left' }, text: { size: 'small', color: color.GRAY_1 } }}
            />
            <Gap height={size.MARGIN_1} />
            <Chip text='준비 중' size='tiny' theme={ETheme.RED} kind='outlined' shape='round' usage='static' />
          </Canvas>
        </>
      )}
      {step === 'plan' && (
        <>
          <TextBlock text='도면 선택' design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }} />
          <Gap height={size.MARGIN_1} />
          <TextBlock
            text='새 작업에 사용할 도면을 선택하세요.'
            design={{ text: { size: 'medium', color: color.GRAY_1 } }}
          />
          <Gap height={size.MARGIN_2} />
          <Scroll direction='y' height='360px' maxHeight='45vh' containerRef={scrollContainerRef}>
            {projectQuery.isLoading ? (
              <PlanItemsContainer>
                <Skeleton tone='normal' height='72px' />
                <Skeleton tone='normal' height='72px' />
                <Skeleton tone='normal' height='72px' />
              </PlanItemsContainer>
            ) : plans.length > 0 ? (
              <PlanItemsContainer>{PlanItems}</PlanItemsContainer>
            ) : (
              <CollectionPlaceholder
                theme={ETheme.CYAN}
                width='100%'
                height='100%'
                icon='files'
                title='등록된 도면이 없어요'
                description='도면 관리에서 첫 도면을 등록해주세요.<br/>등록한 도면으로 작업을 시작할 수 있어요.'
                buttonText='도면 관리로 가기'
                handleButtonClick={() => {
                  dispatch(modalActions.hideModal());
                  navigate(createProjectPath('plan', data.projectKey));
                }}
              />
            )}
          </Scroll>
        </>
      )}
    </Wrapper>
  );
};
export default StartWorkModal;
